import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, input, signal } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  DynamicActionInstance,
  EmailActionInstance,
  EmailActionPreview,
  EmailActionSettings,
  EmailAttachmentMetadata,
  Statement,
} from '@anduin/api';
import { LaunchDarklyFlag, LaunchDarklyState } from '@anduin/auth';
import {
  applicationDebounceTime,
  getControlValueChanges,
  removeArrayItem,
  toggleAbstractControlEnabledState,
  validateEmail,
  validateEmailList,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { Subject, merge } from 'rxjs';
import { filter, map, skip, takeUntil, tap } from 'rxjs/operators';

export enum SmartStatementAction {
  TEST = 'TEST',
  SKIP = 'SKIP',
  SEND = 'SEND',
}

@Component({
  selector: 'statement-preview',
  templateUrl: './statement-preview.component.html',
  styleUrls: ['./statement-preview.component.scss'],
  standalone: false,
})
export class StatementPreviewComponent implements OnInit, OnDestroy {
  PREVIEW_FIELDS = ['body', 'header'].concat(
    this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2)) ? [] : 'subject',
  );

  _statement: Statement | DynamicActionInstance;
  _emailAction: EmailActionInstance;

  mode: 'Preview' | 'Edit' = 'Preview';

  statementForm: UntypedFormGroup = this.fb.group({
    from: this.fb.control(null, { validators: [Validators.required, validateEmail], updateOn: 'blur' }),
    to: this.fb.control(null, { validators: [Validators.required, validateEmailList()], updateOn: 'blur' }),
    ccList: this.fb.control(null, { validators: validateEmailList(), updateOn: 'blur' }),
    bccList: this.fb.control(null, { validators: validateEmailList(), updateOn: 'blur' }),
    subject: this.fb.control(null, { validators: Validators.required, updateOn: 'blur' }),
    header: this.fb.control(null, { validators: Validators.required, updateOn: 'blur' }),
    body: this.fb.control(null, { validators: Validators.required, updateOn: 'blur' }),
  });

  public readonly headlineControl = this.statementForm.get('header');
  public readonly bodyControl = this.statementForm.get('body');

  @Input()
  set preview(preview: string) {
    const tempPreview: SafeHtml = preview ? this.sanitizer.bypassSecurityTrustHtml(preview) : '';
    if (tempPreview !== this.emailPreview) {
      this.emailPreview = tempPreview;
    }
  }

  @Input()
  set emailAction(emailAction: EmailActionInstance) {
    if (emailAction == undefined) {
      return;
    }

    this._emailAction = emailAction;
    this.statementForm.patchValue(
      {
        from: emailAction.from || null,
        to: emailAction.to?.join(', ') || null,
        ccList: emailAction.ccList?.join(', ') || null,
        bccList: emailAction.bccList?.join(', ') || null,
        subject: emailAction.subject || null,
        header: emailAction.header || null,
        body: emailAction.body || null,
      },
      { emitEvent: false },
    );
  }

  @Input()
  set edit(edit: boolean) {
    this.mode = edit ? 'Edit' : 'Preview';
    if (!edit) {
      this.statementForm.disable();
    } else {
      toggleAbstractControlEnabledState(edit && this.canEditAndSendStatements(), this.statementForm);
    }
  }

  @Input()
  set attachments(attachments: EmailAttachmentMetadata[]) {
    this._attachments = attachments;
    this.attachmentsDisplay = [...(attachments || []), ...this.newAttachments];
  }

  private _attachments = this.attachments;
  get attachments(): EmailAttachmentMetadata[] {
    return this._attachments || [];
  }

  @Input()
  previewLoading: boolean;
  @Input()
  noValidEmails: boolean;

  currentActionProcessing = input<SmartStatementAction>(undefined);
  canEditAndSendStatements = input<boolean>(true);
  SmartStatementAction = SmartStatementAction;

  @Output()
  onSendTestEmail = new EventEmitter<EmailActionPreview>();

  @Output()
  onSkipStatement: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  onApproveAction = new EventEmitter<Partial<EmailActionSettings>>();

  @Output()
  onAttachFile = new EventEmitter<File[]>();

  @Output()
  onRemoveFile = new EventEmitter<{ index: number; isAttachmentUploaded: boolean }>();

  @Output()
  onUpdatePreview = new EventEmitter<EmailActionSettings>();

  set newAttachments(newAttachments: File[]) {
    this._newAttachments = newAttachments;
    this.attachmentsDisplay = [...this.attachments, ...(newAttachments || [])];
  }

  private _newAttachments = this.newAttachments;
  get newAttachments(): File[] {
    return this._newAttachments || [];
  }

  attachmentsDisplay: (File | EmailAttachmentMetadata)[] = [];

  unsubscribe$: Subject<void> = new Subject<void>();

  emailPreview: SafeHtml;

  showAlert: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private sanitizer: DomSanitizer,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.statementForm.statusChanges
      .pipe(
        tap((status) => {
          if (status === 'VALID' && this.showAlert) {
            this.showAlert = false;
          }
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();

    if (this.mode === 'Edit') {
      // Listens to non-empty value changes on smart forms, preserving the key for dynamic edit in a tuple
      // Only triggers when all relevant fields for preview are filled
      // Removes leading '$' and escapes single '$' for Kotlin String Templating https://kotlinlang.org/docs/strings.html#string-templates
      merge(
        ...this.PREVIEW_FIELDS.map((key) =>
          getControlValueChanges<string>(this.statementForm.controls[key]).pipe(
            filter(() => this.PREVIEW_FIELDS.every((formKey) => !!this.statementForm.controls[formKey].value)),
            map((value) => [key, value]),
          ),
        ),
      )
        .pipe(
          applicationDebounceTime(),
          skip(1), // I sincerely tried to avoid having to do this.
          tap(([key, value]) => {
            const recipients = this.getRecipients(this.statementForm);

            if (recipients.to.length === 0) {
              return;
            }

            this.onUpdatePreview.emit({
              ...this._emailAction,
              ...this.statementForm.value,
              ...recipients,
              [key]: value.replace(/\$+{/, '${').replace(/(?!\w)\$(?![\w{])/, "${'$$'}"),
            });
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe();
    }
  }

  handleSendTestEmail(): void {
    if (this.statementForm.valid) {
      this.onSendTestEmail.emit({
        emailActionInstance: {
          ...this._emailAction,
          ...this.statementForm.value,
          ...this.getRecipients(this.statementForm),
        },
        htmlPreview: this.emailPreview.toString(),
      });
      this.onAttachFile.emit(this.newAttachments);
      this.newAttachments.length = 0;
    } else {
      this.showAlert = true;
    }
  }

  onSkip(): void {
    this.onSkipStatement.emit();
  }

  /**
   * If form is valid, emit new attachments and statement details.
   */
  onApprove(): void {
    if (this.statementForm.valid) {
      const statementDetails = this.statementForm.value;
      this.onApproveAction.emit({
        ...statementDetails,
        ...this.getRecipients(this.statementForm),
      });

      this.onAttachFile.emit(this.newAttachments);
      this.newAttachments.length = 0;
    } else {
      this.showAlert = true;
    }
  }

  /**
   * Add attachment to new attachments. Multiselect is enabled.
   * @param files Attachment being added. Is undefined if file selection is cancelled.
   * @param fileInput File input element.
   */
  handleAttachments(files: FileList, fileInput: HTMLInputElement): void {
    this.newAttachments = this.newAttachments.concat(Array.from(files));
    fileInput.value = ''; // without this, you cannot reselect the same file after removing it
  }

  /**
   * Remove existing or new attachment.
   * @param index
   * @param attachment
   */
  handleRemoveAttachment(index: number, attachment: EmailAttachmentMetadata | File): void {
    const matchingAttachmentIndex = this.attachments.findIndex((existingAttachment) => existingAttachment === attachment);

    if (matchingAttachmentIndex !== -1) {
      this.attachments = removeArrayItem(this.attachments, matchingAttachmentIndex);
      this.onRemoveFile.emit({ index: matchingAttachmentIndex, isAttachmentUploaded: true });
      return;
    }

    const matchingNewAttachmentIndex = this.newAttachments.findIndex((newAttachment) => newAttachment === attachment);
    const finalIndex = matchingNewAttachmentIndex !== -1 ? matchingNewAttachmentIndex : index;

    this.newAttachments = removeArrayItem(this.newAttachments, finalIndex);
    if (this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))) {
      this.onRemoveFile.emit({ index: finalIndex, isAttachmentUploaded: false });
    }
  }

  /**
   * Convert recipient lists from strings to string arrays.
   * @param form
   */
  getRecipients(form: UntypedFormGroup): { to: string[]; ccList: string[]; bccList: string[] } {
    return {
      to: form.controls.to.value ? form.controls.to.value.split(',').map((value) => value.trim()) : [],
      ccList: form.controls.ccList.value ? form.controls.ccList.value.split(',').map((value) => value.trim()) : [],
      bccList: form.controls.bccList.value ? form.controls.bccList.value.split(',').map((value) => value.trim()) : [],
    };
  }

  /**
   * Clean up subscriptions.
   */
  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
